import { createPortal } from "react-dom";

import sveaLogoSvg from "@bokio/assets/images/svea-logo.svg";
import { Modal, ModalContent } from "@bokio/designsystem/components/Modal";
import { Caption } from "@bokio/designsystem/components/TypographicElements/TypographicElements";

import type { FC, ReactNode } from "react";

import * as styles from "./sveaBankIdModalDialogBase.scss";

export interface SveaBankIdModalDialogBaseProps {
	title: string;
	onClose: () => void;
	header: ReactNode;
	body: ReactNode;
	footerExtra?: ReactNode;
	debugInfo?: Record<string, unknown>;
}

export const SveaBankIdModalDialogBase: FC<SveaBankIdModalDialogBaseProps> = ({
	title,
	onClose,
	header,
	body,
	footerExtra,
	debugInfo,
}) => {
	const showDebugInfo = false;

	return (
		<Modal visible={true} onClose={onClose} title={title}>
			<ModalContent className={styles.background}>
				<svg
					className={styles.backgroundSvg}
					viewBox="0 0 600 600"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					aria-hidden="true"
				>
					{/* Vector 396 (S-curve): Figma position x=-18.78, y=25 within 600Ã—600 container */}
					<g transform="translate(-18.78, 25)">
						<path
							d="M1.20615 126.931C79.2061 21.4307 245.795 -37.2079 351.206 30.4307C447.879 92.4622 433.682 270.122 239.682 280.122C45.6822 290.122 24.3096 473.196 63.6822 518.122C125.307 588.438 294.706 515.43 379.706 435.43C464.706 355.431 525.206 212.431 638.706 212.431"
							stroke="var(--color-primary-500)"
							strokeWidth="3"
						/>
					</g>
					{/* Icon_Star: Figma inset 8.37% 86.36% 83.29% 5.31% â†’ center â‰ˆ(56.86, 75.22), rotate 15deg */}
					<g transform="translate(56.86, 75.22) rotate(15) translate(-20.8333, -20.8333)">
						<path
							d="M16.3196 31.732L6.10226 35.5644L9.93592 25.3471L0 20.8333L9.93592 16.3196L6.10226 6.10226L16.3196 9.93592L20.8333 0L25.3471 9.93592L35.5644 6.10226L31.732 16.3196L41.6667 20.8333L31.732 25.3471L35.5644 35.5644L25.3471 31.732L20.8333 41.6667L16.3196 31.732Z"
							fill="var(--color-shine-300)"
						/>
					</g>
					{/* Icon_Flower: Figma inset 38.91% 5.44% 52.76% 86.22% â†’ center â‰ˆ(542.32, 258.46) */}
					<g transform="translate(542.32, 258.46) translate(-20.834, -20.8335)">
						<path
							d="M20.835 0C25.8688 -2.20036e-07 29.9492 4.66402 29.9492 10.417C29.9492 10.8863 29.9188 11.3479 29.8662 11.8008C30.3195 11.7481 30.7822 11.7187 31.252 11.7188C37.0046 11.7189 41.6678 15.7994 41.668 20.833C41.668 25.8668 37.0048 29.9481 31.252 29.9482C30.7821 29.9482 30.3196 29.917 29.8662 29.8643C29.919 30.3177 29.9492 30.78 29.9492 31.25C29.9492 37.003 25.8688 41.667 20.835 41.667C15.8011 41.667 11.7207 37.003 11.7207 31.25C11.7207 30.7801 11.75 30.3177 11.8027 29.8643C11.3492 29.917 10.887 29.9482 10.417 29.9482C4.66402 29.9482 -2.20036e-07 25.8669 0 20.833C0.000204634 15.7993 4.66415 11.7187 10.417 11.7188C10.8869 11.7188 11.3493 11.748 11.8027 11.8008C11.7501 11.348 11.7207 10.8862 11.7207 10.417C11.7207 4.66403 15.8011 3.19188e-06 20.835 0Z"
							fill="var(--color-shine-300)"
						/>
					</g>
				</svg>
				<div className={styles.card}>
					<div className={styles.cardHeader}>{header}</div>

					{body}

					<div className={styles.cardFooter}>
						{footerExtra}
						<div className={styles.poweredByRow}>
							<Caption>Powered by</Caption>
							<img src={sveaLogoSvg} alt="SVEA" className={styles.poweredByLogo} />
						</div>
					</div>
				</div>
			</ModalContent>
			{showDebugInfo &&
				debugInfo &&
				createPortal(
					<div className={styles.debugOverlay}>
						<div>
							<b>--- DEBUG INFO ---</b>
						</div>
						{Object.entries(debugInfo).map(([key, value]) => (
							<div key={key}>
								<b>{key}:</b> {JSON.stringify(value)}
							</div>
						))}
					</div>,
					document.body,
				)}
		</Modal>
	);
};
